import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { makeStyles } from '@material-ui/styles';
import {
  Button,
  Grid,
  IconButton,
  Link,
  TextField,
  Typography
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import RecaptchaBadge from '../../components/RecaptchaBadge';
import { APP_PATH_LOGIN, MAIN_APP_NAME } from '../../config';
import { useResetPassword } from '../../hooks/useResetPassword';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    height: '100vh',
    minHeight: '100%'
  },
  grid: {
    height: '100%'
  },
  quoteContainer: {
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.background.default}`,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    [theme.breakpoints.down('md')]: {
      display: 'none'
    }
  },
  quote: {
    backgroundColor: theme.palette.neutral,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  quoteInner: {
    textAlign: 'center',
    flexBasis: '600px'
  },
  contentContainer: {
    height: '100%'
  },
  content: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  },
  contentHeader: {
    display: 'flex',
    alignItems: 'center',
    paddingTop: theme.spacing(5),
    paddingBottom: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  },
  contentBody: {
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('md')]: {
      justifyContent: 'center'
    }
  },
  form: {
    paddingLeft: 100,
    paddingRight: 100,
    paddingBottom: 125,
    flexBasis: 700,
    [theme.breakpoints.down('sm')]: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2)
    }
  },
  title: {
    color: theme.palette.text.primary,
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(2)
  },
  textField: {
    marginTop: theme.spacing(2)
  },
  submitButton: {
    margin: theme.spacing(2, 0)
  }
}));

const ResetPassword = () => {
  const classes = useStyles();
  const { formState, handleChange, handleBack, handleSubmit, hasError, isLoading, isSuccess, token } =
    useResetPassword();

  return (
    <div className={classes.root}>
      <Grid className={classes.grid} container justifyContent="center">
        <Grid className={classes.quoteContainer} item lg={5}>
          <div className={classes.quote}>
            <Typography 
              variant="h1" 
              color="textPrimary"
              style={{ fontSize: '3rem', fontWeight: 700 }}>
              {MAIN_APP_NAME}
            </Typography>
          </div>
          <div className={classes.quote}>
            <div className={classes.quoteInner}>
              <Typography className={classes.title} variant="h1">
                Create Your New Password
              </Typography>
              <Typography variant="body1" color="textSecondary">
                Enter a strong password to secure your account.
              </Typography>
            </div>
          </div>
        </Grid>
        <Grid className={classes.contentContainer} item lg={7} xs={12}>
          <div className={classes.content}>
            <div className={classes.contentHeader}>
              <IconButton onClick={handleBack}>
                <ArrowBackIcon />
              </IconButton>
            </div>
            <div className={classes.contentBody}>
              <form className={classes.form} onSubmit={handleSubmit}>
                <Typography className={classes.title} variant="h2">
                  Reset Password
                </Typography>
                <Typography color="textSecondary" gutterBottom>
                  Enter your new password below
                </Typography>
                {!token && (
                  <Typography color="error" gutterBottom style={{ marginTop: 16 }}>
                    Invalid or missing reset token. Please request a new password reset link.
                  </Typography>
                )}
                {isSuccess && (
                  <Typography color="primary" gutterBottom style={{ marginTop: 16 }}>
                    Password reset successful! Redirecting to sign in...
                  </Typography>
                )}
                <TextField
                  className={classes.textField}
                  data-test="new-password"
                  error={hasError('newPassword')}
                  fullWidth
                  helperText={
                    hasError('newPassword') ? formState.errors.newPassword[0] : 'Must be at least 8 characters'
                  }
                  label="New Password"
                  name="newPassword"
                  onChange={handleChange}
                  type="password"
                  value={formState.values.newPassword || ''}
                  variant="outlined"
                />
                <TextField
                  className={classes.textField}
                  data-test="confirm-password"
                  error={hasError('confirmPassword')}
                  fullWidth
                  helperText={
                    hasError('confirmPassword') ? formState.errors.confirmPassword[0] : null
                  }
                  label="Confirm Password"
                  name="confirmPassword"
                  onChange={handleChange}
                  type="password"
                  value={formState.values.confirmPassword || ''}
                  variant="outlined"
                />
                <Button
                  className={classes.submitButton}
                  color="primary"
                  data-test="submit-reset-password"
                  disabled={!formState.isValid || isLoading || !token || isSuccess}
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained">
                  {isLoading ? 'Resetting...' : 'Reset Password'}
                </Button>
                <Typography color="textSecondary" variant="body1">
                  Remember your password?{' '}
                  <Link
                    component={RouterLink}
                    data-test="back-to-login"
                    to={APP_PATH_LOGIN}
                    variant="h6">
                    Sign in
                  </Link>
                </Typography>
                <RecaptchaBadge />
              </form>
            </div>
          </div>
        </Grid>
      </Grid>
    </div>
  );
};

export default ResetPassword;

